import * as React from 'react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

export type StatusBadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info'
type StatusBadgeSize = 'default' | 'prominent'

interface StatusBadgeProps extends Omit<React.ComponentProps<typeof Badge>, 'className' | 'variant'> {
  variant?: StatusBadgeVariant
  size?: StatusBadgeSize
}

const variantStyles: Record<StatusBadgeVariant, string> = {
  default: 'border-badge-neutral-border bg-badge-neutral text-foreground-secondary',
  secondary: 'border-badge-neutral-border bg-badge-neutral text-foreground-secondary',
  destructive: 'border-red-200 bg-red-100 text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-100',
  outline: '',
  success: 'border-emerald-200 bg-emerald-100 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900 dark:text-emerald-100',
  warning: 'border-amber-200 bg-amber-100 text-amber-800 dark:border-amber-800 dark:bg-amber-900 dark:text-amber-100',
  info: 'border-cyan-200 bg-brand-muted text-brand-foreground dark:border-cyan-800 dark:bg-cyan-950 dark:text-cyan-100',
}

const sizeStyles: Record<StatusBadgeSize, string> = {
  default: '',
  prominent: 'px-3 py-1.5 text-body [&>svg]:size-4',
}

/**
 * Domain-status badge. Call sites can choose the standard or page-level
 * prominent treatment; only the semantic tone may vary otherwise.
 */
export function StatusBadge({ variant = 'default', size = 'default', ...props }: StatusBadgeProps) {
  const baseVariant = variant === 'outline' ? 'outline' : 'secondary'

  return (
    <Badge
      data-status-badge=""
      variant={baseVariant}
      className={cn(variantStyles[variant], sizeStyles[size])}
      {...props}
    />
  )
}
